<div class="ws-page ws-page-spacious">
  <header class="page-head">
    <div>
      <p class="kicker">Building enforcement · Sanctioned plans</p>
      <div class="page-head-title-row">
        <app-page-back-btn />
        <h2>Sanctioned plans</h2>
        <app-page-refresh-btn [loading]="loading()" (refresh)="reload()" />
      </div>
      <p class="muted ws-lead">
        One sanctioned plan per plot — choose the plot when uploading, then verify.
      </p>
    </div>
    <div class="plans-count-badge" *ngIf="!loading()">
      <strong>{{ plotCount() }}</strong>
      <span>{{ plotCount() === 1 ? 'plot with plan' : 'plots with plans' }}</span>
    </div>
  </header>

  <p class="error" *ngIf="error()">{{ error() }}</p>

  <section class="page-card plans-tabs-card">
    <nav class="plans-tab-bar" role="tablist" aria-label="Sanctioned plans sections">
      <button
        type="button"
        class="plans-tab"
        role="tab"
        [class.plans-tab-active]="activeTab() === 'browse'"
        [attr.aria-selected]="activeTab() === 'browse'"
        (click)="setTab('browse')"
      >
        All registered plans
        <span class="plans-tab-count">{{ plotCount() }}</span>
      </button>
      <button
        type="button"
        class="plans-tab"
        role="tab"
        [class.plans-tab-active]="activeTab() === 'upload'"
        [attr.aria-selected]="activeTab() === 'upload'"
        (click)="setTab('upload')"
      >
        Upload new plan
      </button>
    </nav>

    <!-- Tab: All registered plans -->
    <div class="plans-tab-panel" role="tabpanel" *ngIf="activeTab() === 'browse'">
      <div class="registry-head">
        <p class="muted ws-lead" *ngIf="!loading()">
          {{
            plotCount() === 0
              ? 'No plans yet — use the Upload new plan tab to get started.'
              : 'Click a plot row to view plan details and the PDF preview.'
          }}
        </p>
      </div>

      <p class="ws-loading" *ngIf="loading()">Loading registered plans…</p>

      <div class="table-wrap" *ngIf="!loading() && displayRows().length">
        <table class="data plans-registry-table">
          <thead>
            <tr>
              <th>Plot</th>
              <th>Plan no</th>
              <th>Status</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let r of displayRows()"
              class="plan-row"
              [class.plan-row-selected]="isPlanSelected(r)"
              tabindex="0"
              role="button"
              (click)="openPlan(r)"
              (keydown.enter)="openPlan(r)"
              (keydown.space)="$event.preventDefault(); openPlan(r)"
            >
              <td>
                <strong class="plot-ref">{{ plotForRef(r.plotRef)?.name || ('Khasra ' + r.plotRef) }}</strong>
                <span class="plot-ref-sub">Khasra {{ r.plotRef }}</span>
              </td>
              <td>{{ r.planNo || '—' }}</td>
              <td>
                <app-status-badge [statusCd]="r.statusCd">{{ planStatusLabel(r.statusCd) }}</app-status-badge>
              </td>
              <td class="text-meta">{{ uploadDateLabel(r) }}</td>
              <td class="plans-row-actions">
                <button
                  type="button"
                  class="btn-link plans-delete-link"
                  *ngIf="canDeletePlans()"
                  [disabled]="deleteImpactLoadingId() === r.id || deletingPlanId() === r.id"
                  (click)="requestDeletePlan(r, $event)"
                >
                  {{
                    deletingPlanId() === r.id
                      ? 'Deleting…'
                      : deleteImpactLoadingId() === r.id
                        ? 'Checking…'
                        : 'Delete'
                  }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="ws-empty" *ngIf="!loading() && !displayRows().length">
        No sanctioned plans registered yet.
        <button type="button" class="link-btn" (click)="setTab('upload')">Upload new plan</button>
      </p>

      <div class="plan-detail-panel plan-detail-in-tab" *ngIf="lastOcr() as ocr">
        <div class="detail-panel-head">
          <div>
            <p class="kicker">Sanctioned plan</p>
            <h3 class="panel-title">
              {{ plotForRef(ocr.plotRef)?.name || ('Khasra ' + ocr.plotRef) }}
            </h3>
            <p class="muted text-meta plan-detail-summary" *ngIf="plotForRef(ocr.plotRef) as plot">
              {{ plotSummaryLine(plot) }}
            </p>
            <p class="muted text-meta">
              {{ planStatusLabel(selectedPlan()?.statusCd || '') }}
              <span *ngIf="selectedPlan()?.planNo"> · Plan no {{ selectedPlan()?.planNo }}</span>
              <span *ngIf="selectedPlan()"> · Uploaded {{ uploadDateLabel(selectedPlan()!) }}</span>
            </p>
          </div>
          <button type="button" class="btn secondary btn-compact" (click)="clearSelectedPlan()">Close</button>
        </div>

        <div class="ocr-layout">
          <div class="ocr-main">
            <ng-container
              *ngTemplateOutlet="
                plotMatchVerdict;
                context: {
                  check: assessPlotMatch(ocr.plotRef, ocr, plotForRef(ocr.plotRef))
                }
              "
            ></ng-container>

            <p class="eyebrow">Extracted data</p>
            <p class="muted">Engine: {{ ocr.engine }} · OCR confidence {{ ocrPct(ocr.ocrConfidence) }}</p>

            <div class="table-wrap" *ngIf="extractedRows(ocr).length; else noExtract">
              <table class="data compact-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of extractedRows(ocr)">
                    <td><strong class="text-primary">{{ row.field }}</strong></td>
                    <td class="num">{{ row.value }}{{ row.unit ? ' ' + row.unit : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <details class="details-block ocr-preview-details" *ngIf="ocrPreview(ocr)">
              <summary>Full text preview ({{ ocrTextLength(ocr) }} characters)</summary>
              <p class="ocr-preview-text">{{ ocrPreview(ocr) }}</p>
            </details>
            <ng-template #noExtract>
              <div class="ws-note">
                <strong>No fields extracted.</strong> Re-process the PDF or verify the document quality, then try again.
              </div>
              <button type="button" class="btn secondary" (click)="digitize(ocr.planId)">Re-process PDF</button>
            </ng-template>

            <div class="ws-subpanel">
              <p class="eyebrow">Verify</p>
              <p class="muted">After verification, proceed to LiDAR scans and measurement comparison.</p>
              <p class="plot-match-warn" *ngIf="verifyBlockedReason(ocr.planId) as reason">{{ reason }}</p>
              <div class="actions">
                <button type="button" class="btn primary" *ngIf="canVerify(ocr.planId)" (click)="verify(ocr.planId, 'verify')">
                  Verify plan
                </button>
                <button
                  type="button"
                  class="btn danger"
                  *ngIf="canVerify(ocr.planId)"
                  (click)="verify(ocr.planId, 'reject')"
                >
                  Reject plan
                </button>
              </div>
            </div>
          </div>

          <aside class="ocr-side">
            <h4 class="panel-title">Document preview</h4>
            <div class="plan-preview-frame" *ngIf="documentPreview() as preview; else noPreview">
              <img
                *ngIf="preview.kind === 'image'"
                class="plan-preview-img"
                [src]="safePreviewUrl(preview.src)"
                alt="Plan document preview"
                (error)="onPreviewImageError()"
              />
              <iframe
                *ngIf="preview.kind === 'pdf'"
                class="plan-preview-pdf"
                [src]="safePreviewUrl(preview.src)"
                title="Plan PDF preview"
              ></iframe>
            </div>
            <ng-template #noPreview>
              <div class="ws-empty">Document preview not available for this plan.</div>
            </ng-template>
            <p class="plan-preview-open" *ngIf="previewOpenUrl() as pdfUrl">
              <a [href]="pdfUrl" target="_blank" rel="noopener">Open PDF in new tab</a>
            </p>
            <p class="muted plan-preview-note" *ngIf="ocr.note">{{ ocr.note }}</p>
          </aside>
        </div>
      </div>
    </div>

    <!-- Tab: Upload new plan -->
    <div class="plans-tab-panel" role="tabpanel" *ngIf="activeTab() === 'upload'">
      <p class="muted ws-lead">Upload a PDF, select the plot, extract fields, then verify.</p>

      <div class="stepper">
        <div class="stepper-item" [class.active]="wizardStep() === 1" [class.done]="wizardStep() > 1">
          <span class="stepper-num">1</span>
          <span class="stepper-title">Upload</span>
          <span class="stepper-sub">Select PDF</span>
        </div>
        <div class="stepper-item" [class.active]="wizardStep() === 2" [class.done]="wizardStep() > 2">
          <span class="stepper-num">2</span>
          <span class="stepper-title">Extract</span>
          <span class="stepper-sub">Review fields</span>
        </div>
        <div class="stepper-item" [class.active]="wizardStep() === 3" [class.done]="wizardStep() > 3">
          <span class="stepper-num">3</span>
          <span class="stepper-title">Verify</span>
          <span class="stepper-sub">Confirm plan</span>
        </div>
      </div>

      <p class="ws-note" *ngIf="session.role() === 'verifier'">
        LiDAR scans are registered separately; compare results on Measurements after verification.
      </p>

      <div *ngIf="wizardStep() === 1">
        <p class="eyebrow">Step 1 — Upload</p>
        <p class="laris-modal-form-error" *ngIf="uploadFormErrors().form" role="alert">
          {{ uploadFormErrors().form }}
        </p>
        <label
          class="drop-zone"
          [class.dragover]="dragOver()"
          [class.laris-field--invalid]="uploadFormErrors().file"
          (dragover)="onDragOver($event)"
          (dragleave)="dragOver.set(false)"
          (drop)="onDrop($event)"
        >
          <input type="file" accept="application/pdf,.pdf" (change)="onFilePicked($event)" />
          <div class="drop-icon" aria-hidden="true">📄</div>
          <p>Drop a PDF file here, or click to browse</p>
          <span class="hint">Accepted format: sanctioned plan PDF</span>
          <div class="file-picked" *ngIf="uploadFile">{{ uploadFile.name }}</div>
          <div class="file-warn" *ngIf="fileWarn()">{{ fileWarn() }}</div>
          <span class="ws-field-error" *ngIf="uploadFormErrors().file">{{ uploadFormErrors().file }}</span>
        </label>
        <div class="meta-row">
          <label [class.laris-field--invalid]="uploadFormErrors().plotRef">
            Plot
            <select
              [(ngModel)]="form.plotRef"
              name="plotRef"
              (ngModelChange)="onPlotRefChange()"
              [disabled]="plotsLoading() || !availablePlotOptions().length"
            >
              <option value="" disabled>
                {{
                  plotsLoading()
                    ? 'Loading plots…'
                    : availablePlotOptions().length
                      ? 'Select plot'
                      : 'All plots already have a plan'
                }}
              </option>
              <option *ngFor="let p of availablePlotOptions()" [value]="p.plotRef">{{ p.label }}</option>
            </select>
            <span class="ws-field-error" *ngIf="uploadFormErrors().plotRef">{{ uploadFormErrors().plotRef }}</span>
            <span class="hint">Select the plot this sanctioned plan belongs to. After upload, the system checks whether the plan matches that plot.</span>
          </label>
          <label>Plan no <input [(ngModel)]="form.planNo" placeholder="Auto-generated if blank" /></label>
        </div>

        <ng-container *ngIf="plotForRef(form.plotRef) as plot">
          <ng-container *ngTemplateOutlet="plotMatchCard; context: { plot: plot }"></ng-container>
        </ng-container>

        <p class="ws-note" *ngIf="!plotsLoading() && !availablePlotOptions().length && plotOptions().length">
          Every registered plot already has a plan. Upload again only if the previous plan was rejected.
        </p>

        <button type="button" class="btn primary" [disabled]="processing()" (click)="uploadPdf()">
          {{ processing() ? 'Extracting…' : 'Upload and extract' }}
        </button>
      </div>

      <div *ngIf="wizardStep() >= 2 && lastOcr() as ocr">
        <ng-container
          *ngTemplateOutlet="
            plotMatchVerdict;
            context: {
              check: assessPlotMatch(ocr.plotRef, ocr, plotForRef(ocr.plotRef))
            }
          "
        ></ng-container>
        <p class="muted upload-success-note">
          Plan #{{ ocr.planId }} uploaded —
          <button type="button" class="link-btn" (click)="openPlanById(ocr.planId)">view in All registered plans</button>.
        </p>
        <button type="button" class="btn secondary" (click)="goToUpload()">Upload another plan</button>
      </div>
    </div>
  </section>
</div>

<app-confirm-dialog
  [open]="deleteConfirmOpen()"
  title="Delete sanctioned plan?"
  [message]="deleteConfirmMessage()"
  confirmLabel="Delete"
  cancelLabel="Cancel"
  [destructive]="true"
  (confirm)="confirmDeletePlan()"
  (cancel)="cancelDeletePlan()"
/>

<ng-template #plotMatchCard let-plot="plot">
  <section class="plot-match-card">
    <div class="plot-match-head">
      <div>
        <p class="eyebrow">Selected plot</p>
        <h4 class="plot-match-title">{{ plot.name }}</h4>
        <p class="muted plot-match-path">{{ plotSummaryLine(plot) }}</p>
      </div>
      <span class="plot-match-badge">Khasra {{ plotRefFromLocation(plot) }}</span>
    </div>

    <div class="plot-match-grid">
      <div class="plot-match-row" *ngFor="let row of plotDetailRows(plot)">
        <span class="plot-match-label">{{ row.label }}</span>
        <span class="plot-match-value">{{ row.value }}</span>
      </div>
    </div>
  </section>
</ng-template>

<ng-template #plotMatchVerdict let-check="check">
  <section class="plot-match-verdict" [ngClass]="plotMatchVerdictClass(check.verdict)">
    <p class="eyebrow">Does this plan match the selected plot?</p>
    <h4 class="plot-match-verdict-title">{{ check.headline }}</h4>
    <p class="plot-match-verdict-detail">{{ check.detail }}</p>

    <div class="table-wrap" *ngIf="check.checks.length">
      <table class="data compact-table plot-match-check-table">
        <thead>
          <tr>
            <th>Check</th>
            <th>In PDF</th>
            <th>Master plot</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of check.checks">
            <td>{{ row.label }}</td>
            <td>{{ row.pdf }}</td>
            <td>{{ row.master }}</td>
            <td>
              <span class="match-pill match-pill--ok" *ngIf="row.ok === true">Match</span>
              <span class="match-pill match-pill--bad" *ngIf="row.ok === false">Mismatch</span>
              <span class="match-pill match-pill--warn" *ngIf="row.ok === null">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</ng-template>

<div class="toast" *ngIf="toast()">{{ toast() }}</div>
